// frontend/src/components/SaveTreatmentPlanModal.js

import React, { useState } from "react";
import {
  XMarkIcon,
  CheckIcon,
  ClipboardDocumentListIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import { httpService } from "../../services/httpService";

const SaveTreatmentPlanModal = ({ isOpen, onClose, onSave, procedures, products }) => {
  const [planName, setPlanName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!planName.trim()) {
      toast.error("Please enter a plan name");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: planName.trim(),
        description: description.trim(),
        status: "active",
        procedures: procedures.map((p) => ({
          procedure_id: p.procedure_id,
          occurrences: p.occurrences || 1,
          override_price: {
            low: p.price || 0,
            high: p.price || 0,
          },
        })),
        products: products.map((p) => ({
          product_id: p._id,
          quantity: p.quantity || 1,
          price: p.price || 0,
          usage_qty: parseFloat(p.usage_qty) || 0,
          usage_unit: p.usage_unit || "",
        })),
      };

      const response = await httpService.post(
        "/vendorvetclinicservice/add-treatment-plan",
        payload
      );

      if (response.status) {
        toast.success(`Treatment plan "${planName}" created successfully!`);
        onSave(response.data);
        onClose();
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to create treatment plan");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <ClipboardDocumentListIcon className="w-5 h-5 text-emerald-600" />
            <h2 className="text-base font-bold text-slate-800">Save as Treatment Plan</h2>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="text-[11px] text-slate-500 bg-emerald-50 p-3 rounded-lg border border-emerald-100">
            <p className="font-medium text-emerald-700 mb-1.5">You're about to save:</p>

            {procedures.map((p) => (
              <div key={p.row_id} className="flex items-center justify-between py-0.5 text-slate-600">
                <span>
                  {p.procedure_name}
                  {p.occurrences > 1 ? ` × ${p.occurrences}` : ""}
                </span>
                <span className="font-semibold text-slate-700">
                  ₹{((p.price || 0) * (p.occurrences || 1)).toFixed(2)}
                </span>
              </div>
            ))}

            {products.map((prod) => (
              <div key={prod._id} className="flex items-center justify-between py-0.5 text-slate-600">
                <span>
                  {prod.name}
                  {prod.quantity > 1 ? ` × ${prod.quantity}` : ""}
                </span>
                <span className="font-semibold text-slate-700">
                  ₹{((prod.price || 0) * (prod.quantity || 1)).toFixed(2)}
                </span>
              </div>
            ))}

            <div className="flex items-center justify-between mt-2 pt-2 border-t border-emerald-200 font-bold text-emerald-700">
              <span>Total</span>
              <span>
                ₹{(
                  procedures.reduce((sum, p) => sum + (p.price || 0) * (p.occurrences || 1), 0) +
                  products.reduce((sum, p) => sum + (p.price || 0) * (p.quantity || 1), 0)
                ).toFixed(2)}
              </span>
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">
              Plan Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={planName}
              onChange={(e) => setPlanName(e.target.value)}
              placeholder="e.g., Annual Checkup, Dental Treatment"
              className="w-full text-[12px] text-slate-700 border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-transparent"
              autoFocus
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-slate-100 bg-slate-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-[12px] font-medium text-slate-500 hover:text-slate-700"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !planName.trim()}
            className="flex items-center gap-2 px-4 py-2 text-[12px] font-semibold text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 disabled:opacity-50 transition-colors"
          >
            {saving ? "Saving..." : <><CheckIcon className="w-3.5 h-3.5" /> Save Plan</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SaveTreatmentPlanModal;